import { MenuPositions, MenuRepositionModes } from "./menu.options.js";
import { CollisionEdges, getBoundaryBounds, getAvailableSpace, detectCollisionEdgeFromIntersection, detectCollisionWithBoundary, DEFAULT_ROOT_MARGIN } from "../../util/positioning.js";
// Re-export collision edge types for consumers
export { CollisionEdges } from "../../util/positioning.js";
/**
 * Positioning manager for menu component
 * Handles collision detection and repositioning logic
 * @internal
 */
export class MenuPositioning {
    set position(value) {
        this._position = value;
    }
    get position() {
        return this._position;
    }
    set repositionMode(value) {
        this._repositionMode = value;
    }
    get repositionMode() {
        return this._repositionMode;
    }
    constructor(options) {
        var _a, _b;
        this.rootMargin = DEFAULT_ROOT_MARGIN;
        this.minMenuHeight = 100;
        this._position = MenuPositions.below;
        this._repositionMode = MenuRepositionModes.auto;
        /**
         * Gets the boundary bounds for overflow calculations.
         * Uses custom overflow boundary if set, otherwise uses viewport.
         * @returns BoundaryBounds object
         */
        this.getBoundaryBounds = () => {
            return getBoundaryBounds(this.overflowBoundaryReference);
        };
        /**
         * Checks if a position is valid (menu fits within boundary)
         * @param openPosition - The position to check
         * @returns Whether the position is valid
         */
        this.checkPosition = (openPosition) => {
            var _a, _b, _c;
            const menuRect = (_a = this.menuReference) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const anchorRect = (_b = this.anchorReference) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
            if (menuRect && anchorRect) {
                const boundary = this.getBoundaryBounds();
                const space = getAvailableSpace(anchorRect, boundary, this.rootMargin);
                const positionChecks = {
                    [MenuPositions.above]: menuRect.height < space.above,
                    [MenuPositions.below]: menuRect.height < space.below
                };
                return (_c = positionChecks[openPosition]) !== null && _c !== void 0 ? _c : false;
            }
            return false;
        };
        /**
         * Gets the opposite position for flipping
         * @param collisionEdge - The edge where collision occurred
         * @returns The opposite position
         */
        this.oppositePosition = (collisionEdge) => {
            switch (collisionEdge) {
                case CollisionEdges.top:
                    return MenuPositions.below;
                case CollisionEdges.bottom:
                    return MenuPositions.above;
                default:
                    return this._position;
            }
        };
        /**
         * Gets open positions based on collision edge
         * @param collisionEdge - The edge where collision occurred
         * @returns Array of valid positions
         */
        this.getOpenPositionsFromEdge = (collisionEdge) => {
            if (collisionEdge) {
                const opposite = this.oppositePosition(collisionEdge);
                if (this.checkPosition(opposite)) {
                    return [opposite];
                }
            }
            return [this._position];
        };
        /**
         * Gets all currently valid positions
         * @returns Array of valid positions
         */
        this.getOpenPositions = () => {
            const positions = [];
            for (const key in MenuPositions) {
                const pos = MenuPositions[key];
                if (this.checkPosition(pos)) {
                    positions.push(pos);
                }
            }
            return positions;
        };
        /**
         * Finds valid positions based on intersection collision
         * @param entry - The intersection observer entry
         * @returns Array of valid positions
         */
        this.findOpenPositionsFromCollision = (entry) => {
            const intersectingEdge = detectCollisionEdgeFromIntersection(entry);
            return this.getOpenPositionsFromEdge(intersectingEdge);
        };
        /**
         * Calculates available space for the menu in a given position
         * @param position - The position to calculate space for
         * @returns Available height in pixels
         */
        this.getMenuAvailableSpace = (position) => {
            var _a;
            const anchorRect = (_a = this.anchorReference) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (!anchorRect) {
                return 0;
            }
            const boundary = this.getBoundaryBounds();
            const space = getAvailableSpace(anchorRect, boundary, this.rootMargin);
            switch (position) {
                case MenuPositions.above:
                    return space.above;
                case MenuPositions.below:
                    return space.below;
                default:
                    return boundary.bottom - boundary.top - this.rootMargin;
            }
        };
        /**
         * Determines if menu needs to be constrained with max-height
         * @returns Object with needsConstraint flag and maxHeight value
         */
        this.calculateConstrainedHeight = () => {
            var _a;
            const menuRect = (_a = this.menuReference) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (!menuRect) {
                return { needsConstraint: false, maxHeight: null };
            }
            const currentPosition = this._position;
            const currentSpace = this.getMenuAvailableSpace(currentPosition);
            // If menu fits in current position, no constraint needed
            if (menuRect.height <= currentSpace) {
                return { needsConstraint: false, maxHeight: null };
            }
            const oppositePosition = currentPosition === MenuPositions.below
                ? MenuPositions.above
                : MenuPositions.below;
            const oppositeSpace = this.getMenuAvailableSpace(oppositePosition);
            // If opposite position has more space and menu fits there, flip will handle it
            if (oppositeSpace > currentSpace && menuRect.height <= oppositeSpace) {
                return { needsConstraint: false, maxHeight: null };
            }
            // Menu doesn't fit in either position, constrain to the larger space
            const maxSpace = Math.max(currentSpace, oppositeSpace);
            // Only constrain if we have at least minimum space
            if (maxSpace >= this.minMenuHeight) {
                return { needsConstraint: true, maxHeight: maxSpace };
            }
            return { needsConstraint: false, maxHeight: null };
        };
        /**
         * Checks if the menu overflows the boundary and returns the appropriate position.
         * This uses direct rect calculations, which is needed for custom boundaries
         * because popovers render in the top layer (outside the DOM hierarchy)
         * and IntersectionObserver with a custom root won't work correctly.
         * @returns Object with hasOverflow flag, collisionEdge, and suggested position
         */
        this.checkOverflowWithBoundary = () => {
            var _a;
            const menuRect = (_a = this.menuReference) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (!menuRect) {
                return {
                    hasOverflow: false,
                    collisionEdge: undefined,
                    suggestedPosition: undefined
                };
            }
            const boundary = this.getBoundaryBounds();
            const { hasOverflow, collisionEdge } = detectCollisionWithBoundary(menuRect, boundary);
            if (!hasOverflow) {
                return {
                    hasOverflow: false,
                    collisionEdge: undefined,
                    suggestedPosition: undefined
                };
            }
            // Get the opposite position
            const oppositePosition = this.oppositePosition(collisionEdge);
            // Check if opposite position is valid
            const suggestedPosition = this.checkPosition(oppositePosition)
                ? oppositePosition
                : undefined;
            return {
                hasOverflow: true,
                collisionEdge,
                suggestedPosition
            };
        };
        this.menuReference = options.menuReference;
        this.anchorReference = options.anchorReference;
        this.overflowBoundaryReference = (_a = options.overflowBoundaryReference) !== null && _a !== void 0 ? _a : null;
        this._repositionMode = (_b = options.repositionMode) !== null && _b !== void 0 ? _b : MenuRepositionModes.auto;
    }
}
//# sourceMappingURL=menu.positioning.js.map